import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import ErrorBoundary from '../components/ErrorBoundary';
import PanoramaViewer, { type PanoramaFrame } from '../components/PanoramaViewer';
import TimeSlider from '../components/TimeSlider';
import { pt } from '../copy';
import { fetchJourney, frameAbsoluteUrl, type RemoteJourney } from '../lib/api';
import { yearToHumanLabel } from '../lib/prompt';

type LoadState =
  | { status: 'loading' }
  | { status: 'ready'; remote: RemoteJourney }
  | { status: 'gallery-error'; remote: RemoteJourney }
  | { status: 'error' };

export default function Journey() {
  const { id } = useParams<{ id: string }>();
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [t, setT] = useState(0);

  useEffect(() => {
    if (!id) {
      setState({ status: 'error' });
      return;
    }

    const controller = new AbortController();
    setState({ status: 'loading' });
    setT(0);

    void fetchJourney(id, controller.signal)
      .then((remote) => {
        if (!controller.signal.aborted) {
          setState({ status: 'ready', remote });
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setState({ status: 'error' });
        }
      });

    return () => controller.abort();
  }, [id]);

  if (state.status === 'loading') {
    return (
      <main className="grid min-h-screen place-items-center bg-black px-6 text-white">
        <div className="flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-5 py-3 text-sm text-white/80">
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/25 border-t-white" />
          <span>{pt.generateCtaLoading}</span>
        </div>
      </main>
    );
  }

  if (state.status === 'error') {
    return (
      <main className="grid min-h-screen place-items-center bg-black px-6 text-white">
        <div className="space-y-4 text-center">
          <p className="text-lg text-white/80">{pt.journeyNotFound}</p>
          <Link
            className="inline-flex rounded-full border border-white/10 bg-white px-4 py-2 text-sm font-semibold text-black transition hover:bg-white/90"
            to="/"
          >
            {pt.backToHome}
          </Link>
        </div>
      </main>
    );
  }

  const remoteJourney = state.remote;
  const galleryUnavailable = state.status === 'gallery-error';
  const years = state.remote.frames.map((frame) => frame.year);
  const firstYear = years[0];
  const lastYear = years[years.length - 1];
  const panoramaFrames: PanoramaFrame[] = remoteJourney.frames.map((frame) => ({
    id: String(frame.year),
    src: frameAbsoluteUrl(frame.url),
    year: frame.year,
  }));

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-black text-white">
      <main className="grid min-h-screen grid-cols-12 gap-4 p-4">
        <section className="col-span-12 min-w-0 space-y-4 lg:col-span-4 lg:pr-2">
          <div className="rounded-3xl border border-white/10 bg-white/5 p-5 backdrop-blur-xl">
            <p className="text-xs uppercase tracking-[0.28em] text-white/45">{pt.appTitle}</p>
            <h1 className="mt-3 text-3xl font-semibold tracking-tight text-white">
              {remoteJourney.location}
            </h1>
            <p className="mt-3 text-sm leading-6 text-white/65">{remoteJourney.prompt}</p>
            <p className="mt-4 text-xs text-white/45">
              {firstYear !== undefined && lastYear !== undefined
                ? `${yearToHumanLabel(firstYear)} → ${yearToHumanLabel(lastYear)}`
                : '—'}
            </p>
          </div>

          <div className="rounded-3xl border border-white/10 bg-white/5 p-5 text-sm text-white/60 backdrop-blur-xl">
            <p>{pt.footerCredits}</p>
          </div>

          <Link
            className="inline-flex rounded-full border border-white/10 bg-white/10 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/15"
            to="/"
          >
            {pt.backToHome}
          </Link>
        </section>

        <section className="relative col-span-12 min-h-[55vh] min-w-0 lg:col-span-8">
          {galleryUnavailable ? (
            <div className="grid h-full min-h-[55vh] place-items-center rounded-2xl border border-white/10 bg-white/5 p-6 text-center backdrop-blur-xl">
              <p className="max-w-md text-lg leading-7 text-white/80">
                {pt.galleryTemporarilyUnavailable}
              </p>
            </div>
          ) : (
            <>
              <ErrorBoundary>
                <PanoramaViewer
                  className="h-full w-full overflow-hidden rounded-2xl"
                  frames={panoramaFrames}
                  blendT={t}
                  hint={pt.viewerHint}
                  onError={() => {
                    setState((current) =>
                      current.status === 'ready'
                        ? { status: 'gallery-error', remote: current.remote }
                        : current,
                    );
                  }}
                />
              </ErrorBoundary>

              <div className="absolute inset-x-4 bottom-4">
                <TimeSlider t={t} years={years} onChange={setT} />
              </div>
            </>
          )}
        </section>
      </main>
    </div>
  );
}
